import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { OrganizationAssignmentStateFilter } from '@today/types'
import type { OrgAssignmentRowView, OrgOption } from '@today/ui-domain'
import { useMemo, useState } from 'react'
import { type OrgAssignmentDTO, type OrgUserDTO, organizationService } from '@/services/organization.service'
import {
	type AssignmentFormValues,
	assignmentFormErrors,
	buildAssignmentBody,
	canMakePrimary,
	displayNameOf,
	EMPTY_ASSIGNMENT_FORM,
	isCurrent,
} from './assignment-form'
import { listState, type OrgT, useConfirmState, useEditorState, viewConfirm, viewEditor } from './org-editor'
import { toMutationError } from './org-errors'

const dateLabel = (iso: string | null, locale: string) => (iso ? new Date(iso).toLocaleDateString(locale) : null)

const toRow = (a: OrgAssignmentDTO, names: ReadonlyMap<string, string>, locale: string): OrgAssignmentRowView => {
	const now = new Date()
	return {
		id: a.id,
		userLabel: displayNameOf(names, a.userId),
		assignmentType: a.assignmentType,
		isPrimary: a.isPrimary,
		validFrom: dateLabel(a.validFrom, locale) ?? '',
		validTo: dateLabel(a.validTo, locale),
		current: isCurrent(a, now),
		canMakePrimary: canMakePrimary(a, now),
	}
}

/** The tenant's users (only with `user.read`), as a name map and picker options. */
function useOrgUsers(slug: string, enabled: boolean) {
	const users = useQuery({ queryKey: ['org-users', slug], queryFn: () => organizationService.users(slug), enabled })
	return useMemo(() => {
		const list: OrgUserDTO[] = users.data ?? []
		const names = new Map(list.map((u) => [u.userId, u.displayName]))
		const options: OrgOption[] | null = enabled ? list.map((u) => ({ id: u.userId, label: u.displayName })) : null
		return { names, options }
	}, [users.data, enabled])
}

interface AssignmentsInput {
	unitId: string
	unitCode: string
	slug: string
	userDirectory: boolean
	t: OrgT
	locale: string
}

/** Assign / end / make-primary: each success refetches the unit's assignments. */
function useAssignmentMutations(unitId: string, unitCode: string, t: OrgT) {
	const client = useQueryClient()
	const form = useEditorState<AssignmentFormValues>(t)
	const ending = useConfirmState(t)
	const [rowError, setRowError] = useState<string | null>(null)
	const after = (close: () => void) => async () => {
		close()
		setRowError(null)
		await client.invalidateQueries({ queryKey: ['org-assignments', unitId] })
	}
	const create = useMutation({
		mutationFn: (values: AssignmentFormValues) =>
			organizationService.createAssignment(buildAssignmentBody(values, unitCode)),
		onSuccess: after(form.close),
		onError: form.refused,
	})
	const end = useMutation({
		mutationFn: (id: string) => organizationService.endAssignment(id),
		onSuccess: after(ending.cancel),
		onError: ending.refused,
	})
	const primary = useMutation({
		mutationFn: (id: string) => organizationService.makePrimary(id),
		onSuccess: after(() => undefined),
		onError: (err) => setRowError(toMutationError(err, t).message),
	})
	return { form, ending, rowError, create, end, primary }
}

/** A unit's Assigned users panel (REQ-008): state filter, assign, end and make primary. */
export function useOrgAssignments({ unitId, unitCode, slug, userDirectory, t, locale }: AssignmentsInput) {
	const [stateFilter, setStateFilter] = useState(OrganizationAssignmentStateFilter.CURRENT)
	const list = useQuery({
		queryKey: ['org-assignments', unitId, stateFilter],
		queryFn: () => organizationService.assignments(unitId, stateFilter),
	})
	const users = useOrgUsers(slug, userDirectory)
	const { form, ending, rowError, create, end, primary } = useAssignmentMutations(unitId, unitCode, t)
	const rows = useMemo(
		() => (list.data ?? []).map((a) => toRow(a, users.names, locale)),
		[list.data, users.names, locale],
	)
	return {
		state: listState(list),
		stateFilter,
		setStateFilter,
		rows,
		rowError,
		userOptions: users.options,
		retry: () => void list.refetch(),
		editor: viewEditor(form.editor),
		ending: viewConfirm(ending.pending),
		isBusy: create.isPending || end.isPending || primary.isPending,
		openAssign: () => form.open('create', EMPTY_ASSIGNMENT_FORM, {}),
		change: form.change,
		close: form.close,
		submit: () => {
			if (!form.editor) return
			const errors = assignmentFormErrors(form.editor.values, { userDirectory })
			if (Object.keys(errors).length > 0) return form.invalid(errors)
			create.mutate(form.editor.values)
		},
		askEnd: (id: string) => ending.ask(id, rows.find((r) => r.id === id)?.userLabel ?? ''),
		cancelEnd: ending.cancel,
		confirmEnd: () => ending.pending && end.mutate(ending.pending.id),
		makePrimary: (id: string) => primary.mutate(id),
	}
}
